import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ThumbsUp, Eye, Send, Radio } from "lucide-react";
import { api } from "../api";
import { useAuth, useGuard } from "../auth";
import { subscribe, unsubscribe, onEvent } from "../socket";
import { fmtNum, timeAgo } from "../config";
import { LiveCard, Avatar, SectionTitle, Spinner, Empty } from "../components/cards";

export const LiveList = () => {
  const [streams, setStreams] = useState(null);
  useEffect(() => {
    api.get("/live/streams").then((r) => setStreams(Array.isArray(r) ? r : r.streams || [])).catch(() => setStreams([]));
  }, []);
  if (streams === null) return <Spinner />;
  const liveNow = streams.filter((s) => s.isLive || s.status === "live");
  const past = streams.filter((s) => !(s.isLive || s.status === "live"));

  return (
    <div className="mx-auto max-w-7xl" data-testid="live-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Live</h1>
        <div className="flex items-center gap-2">
          <Link to="/app/past-live" data-testid="live-past-link" className="rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 transition-colors hover:border-crimson/50">
            Past Live
          </Link>
          <Link to="/app/go-live" data-testid="live-golive-button" className="flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover">
            <Radio size={14} /> Go Live
          </Link>
        </div>
      </div>
      <SectionTitle>Live now</SectionTitle>
      {liveNow.length === 0 ? (
        <Empty>No one is live right now — check back soon.</Empty>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {liveNow.map((s) => <LiveCard key={s.id} stream={s} />)}
        </div>
      )}
      <div className="mt-10">
        <SectionTitle>Past broadcasts</SectionTitle>
        {past.length === 0 ? (
          <Empty>No past broadcasts yet.</Empty>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {past.map((s) => <LiveCard key={s.id} stream={s} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export const LiveDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const guard = useGuard();
  const [stream, setStream] = useState(null);
  const [chat, setChat] = useState([]);
  const [text, setText] = useState("");
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [viewers, setViewers] = useState(0);
  const chatEndRef = useRef(null);

  useEffect(() => {
    setStream(null);
    setChat([]);
    api.get(`/live/streams/${id}`).then((s) => {
      setStream(s);
      setLiked(!!s.liked);
      setLikes(s.likeCount ?? s.likes ?? 0);
      setViewers(s.viewers ?? 0);
    }).catch(() => {});
    api.get(`/live/streams/${id}/chat`).then((r) => setChat(Array.isArray(r) ? r : r.messages || [])).catch(() => {});

    subscribe(`live:${id}`);
    const offChat = onEvent("live_chat", (ev) => {
      if (ev.message) setChat((c) => [...c, ev.message]);
    });
    const offViewers = onEvent("viewers", (ev) => {
      if (ev.viewers != null) setViewers(ev.viewers);
    });
    const offLikes = onEvent("live_likes", (ev) => {
      if (ev.likes != null) setLikes(ev.likes);
    });
    const offStatus = onEvent("live_status", (ev) => {
      if (ev.status) setStream((s) => (s ? { ...s, status: ev.status, isLive: ev.status === "live" } : s));
    });
    return () => {
      unsubscribe(`live:${id}`);
      offChat();
      offViewers();
      offLikes();
      offStatus();
    };
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.length]);

  if (!stream) return <Spinner />;
  const live = stream.isLive || stream.status === "live";
  const isOwner = user && (stream.creator?.id || stream.creatorId) === user.id;
  const isAdmin = user && (user.isAdmin || user.isSuperAdmin);

  const endStream = async () => {
    try {
      await api.post(`/live/streams/${id}/end`);
      setStream((s) => (s ? { ...s, status: "ended", isLive: false } : s));
    } catch {}
  };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!guard()) return;
    if (!text.trim()) return;
    try {
      await api.post(`/live/streams/${id}/chat`, { text: text.trim() });
      setText("");
    } catch {}
  };

  const toggleLike = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/live/streams/${id}/like`);
      setLiked(r.liked ?? !liked);
      if (r.likeCount != null) setLikes(r.likeCount);
    } catch {}
  };

  return (
    <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1fr_340px]" data-testid="live-detail-page">
      <div>
        <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${stream.youtubeId}?autoplay=1&playsinline=1&rel=0`}
            title={stream.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
            data-testid="live-player"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-widest ${live ? "bg-crimson text-white" : "bg-white/10 text-slate-400"}`} data-testid="live-status-badge">
                {live ? "Live" : "Past broadcast"}
              </span>
              <h1 className="font-heading text-lg font-bold tracking-tight text-white sm:text-xl">{stream.title}</h1>
            </div>
            <Link to={`/app/creator/${stream.creator?.id || stream.creatorId}`} className="mt-2 flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <Avatar user={stream.creator} size={26} />
              {stream.creator?.displayName || stream.creator?.username}
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {live && isOwner && (
              <button
                onClick={endStream}
                data-testid="live-end-button"
                className="rounded-full bg-crimson px-4 py-2 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover"
              >
                End stream
              </button>
            )}
            <button
              onClick={toggleLike}
              data-testid="live-like-button"
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${liked ? "border-crimson/50 bg-crimson/15 text-crimson" : "border-white/10 text-slate-300 hover:border-white/25"}`}
            >
              <ThumbsUp size={15} fill={liked ? "currentColor" : "none"} /> {fmtNum(likes)}
            </button>
            <span className="flex items-center gap-2 text-sm text-slate-500" data-testid="live-viewers">
              <Eye size={15} /> {fmtNum(viewers)} watching
            </span>
          </div>
        </div>
      </div>

      <aside className="flex h-[560px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="live-chat">
        <p className="border-b border-white/10 px-4 py-3 font-heading text-sm font-bold text-white">Live chat</p>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {chat.map((m, i) => (
            <div key={m.id || i} className="text-sm">
              <span className="font-semibold text-crimson">{(m.user || m.sender)?.displayName || m.displayName || m.username || "Viewer"} </span>
              <span className="text-slate-300">{m.text || m.content}</span>
              <span className="ml-2 font-mono text-[9px] text-slate-600">{timeAgo(m.createdAt)}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <form onSubmit={sendChat} className="flex gap-2 border-t border-white/10 p-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => !user && guard()}
            placeholder={user ? "Say something…" : "Sign in to chat…"}
            data-testid="live-chat-input"
            className="flex-1 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
          />
          <button className="rounded-full bg-crimson p-2.5 text-white hover:bg-crimson-hover" data-testid="live-chat-send">
            <Send size={15} />
          </button>
        </form>
      </aside>
    </div>
  );
};
